import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {flatMap, forkJoin, Observable, of} from 'rxjs';
import {JobDocuments} from '../models/documents/jobDocuments.model';
import {CreateDocumentRequest} from '../models/documents/createDocument.request';
import {UpdateDocumentRequest} from '../models/documents/updateDocument.request';
import {map, mergeMap, tap} from 'rxjs/operators';
import {Document} from '../models/documents/document.model';
import {deepCopy} from '../utils/deepCopy';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  private BASE_SERVICE: string = environment.BASE_URL + 'job/';
  private DOCUMENT_SERVICE = '/document';
  private DOCUMENTS_SERVICE = '/documents';

  constructor(private http: HttpClient) {
  }

  public getDocuments(jobId: string): Observable<JobDocuments> {
    return this.http.get<JobDocuments>(`${this.BASE_SERVICE}${jobId}${this.DOCUMENT_SERVICE}`);
  }

  public getDocument(jobId: string, id: string): Observable<string> {
    return this.http.get(`${this.BASE_SERVICE}${jobId}${this.DOCUMENT_SERVICE}/${id}`, {responseType: 'text'});
  }

  public createDocument(jobId: string, request: CreateDocumentRequest, file?: File): Observable<JobDocuments> {
    return this.http.post<JobDocuments>(`${this.BASE_SERVICE}${jobId}${this.DOCUMENT_SERVICE}`, request).pipe(
      request.type === 'FILE' ?
        mergeMap((response) => this.uploadFile(response, file)) :
        map(response => response)
    );
  }

  public updateDocument(jobId: string, id: string, request: UpdateDocumentRequest): Observable<JobDocuments> {
    return this.http.patch<JobDocuments>(`${this.BASE_SERVICE}${jobId}${this.DOCUMENT_SERVICE}/${id}`, request).pipe(
    );
  }

  public deleteDocument(jobId: string, id: string): Observable<JobDocuments> {
    return this.http.delete<JobDocuments>(`${this.BASE_SERVICE}${jobId}${this.DOCUMENT_SERVICE}/${id}`).pipe(
    );
  }

  public deleteDocuments(jobId: string, documents: Document[]): Observable<JobDocuments> {
    return this.http.post<JobDocuments>(`${this.BASE_SERVICE}${jobId}${this.DOCUMENTS_SERVICE}/delete`, {
      documents: documents.map(doc => doc.id)
    }).pipe(
    );
  }

  public downloadDocumentsZip(jobId: string, documents: Document[]): Observable<string[]> {
    const urlsObservable = documents.map((doc) => {
      return this.getDocument(jobId, doc.id);
    });

    return forkJoin(urlsObservable);
  }

  private uploadFile(jobDocuments: JobDocuments, file: File): Observable<JobDocuments> {
    const index = jobDocuments.documents.findIndex(doc => !!doc.uploadUrl);
    let document: Document = jobDocuments.documents[index];
    if (document) {
      return this.http.put(document.uploadUrl, file, {
        headers: {
          'content-type': file.type,
        },
        reportProgress: true,
        observe: 'events',
      }).pipe(
        map((res: any) => {
          document = deepCopy(document);
          if (res.type === 1) {
            document.uploadPercentage = Math.round(res.loaded / res.total * 10000.00) / 100.00;
            document.uploading = true;
          }
          if (res.loaded === res.total) {
            document.uploading = false;
          }
          const newJobDocuments: JobDocuments = deepCopy(jobDocuments);
          newJobDocuments.documents[index] = document;
          return newJobDocuments;
        })
      );
    }
    return of(jobDocuments);
  }
}
